import type { IConversation } from 'src/interfaces/conversation';
import {
    getActiveAreaChannels,
    isChannelsModeArea,
} from 'src/modules/conversations/channels';
import type { IMessage } from 'src/interfaces/message';
import type {
    ConversationTag,
    MessageOrigin,
    ThreadMessage,
    ThreadSummary,
} from '../types';

type UnknownRecord = Record<string, unknown>;

type ConversationChannelLike = {
    channel?: string;
    isActive?: boolean;
    sender?: {
        phone?: string;
    };
};

type StoreAreaLike = {
    name?: string;
    phone?: string;
    conversationMode?: string;
    conversationChannels?: ConversationChannelLike[];
};

type ContactLike = {
    _id?: string;
    name?: string;
    displayName?: string;
    phone?: string;
    normalizedPhone?: string;
    image?: string;
};

type ConversationLike = Partial<
    Omit<IConversation, 'lastMessage' | 'messages' | 'tags' | 'lead' | 'area'>
> & {
    id?: string;
    area?: string | { name?: string };
    contact?: ContactLike;
    linkedContact?: ContactLike | string;
    client?: ContactLike;
    lead?: ContactLike | string;
    recipient?: {
        normalizedPhone?: string;
    };
    providers?: Array<{
        name?: string;
        senderPhone?: string;
    }>;
    lastProvider?: string;
    provider?: string;
    image?: string;
    tags?: ConversationTag[];
    lastMessage?: MessageLike | string | Date;
    messages?: MessageLike[];
    latestMessage?: MessageLike;
    message?: MessageLike;
    lastMessageText?: string;
    lastMessageBody?: string;
    lastMessagePreview?: string;
    lastMessageAt?: Date | string;
    isSilenced?: boolean;
    status?: string;
    assignedTo?: string | { presentationCard?: string };
};

type MessageLike = Partial<IMessage> & {
    id?: string;
    name?: string;
    author?: string;
    from?: string;
    body?: string;
    message?: string;
    text?: string;
    content?:
        | string
        | {
              text?: string;
              media?: Array<{
                  url?: string;
                  contentType?: string;
              }>;
          };
    mediaUrl?: string;
    mediaContentType?: string;
    MediaContentType0?: string;
    media?: {
        url?: string;
        contentType?: string;
    };
    direction?: string;
    created_at?: Date | string;
    isSeen?: boolean;
    kind?: ThreadKind;
    reaction?: string;
    reactions?: Array<{
        emoji?: string;
        createdAt?: Date | string;
    }>;
};

const isRecord = (value: unknown): value is UnknownRecord =>
    typeof value === 'object' && value !== null;

const getString = (value: unknown): string | undefined =>
    typeof value === 'string' && value.trim() ? value : undefined;

const normalizePhone = (phone?: string): string =>
    String(phone || '')
        .replace('whatsapp:', '')
        .trim();

const getId = (value: unknown): string | undefined => {
    if (typeof value === 'string' && value.trim()) return value;
    if (typeof value === 'number') return String(value);

    return undefined;
};

const getDocumentId = (value: unknown): string | undefined => {
    const directId = getId(value);
    if (directId) return directId;
    if (!isRecord(value)) return undefined;

    return getId(value._id) || getId(value.id);
};

const normalizeCollection = <T>(collection: unknown, keys: string[]): T[] => {
    if (Array.isArray(collection)) return collection as T[];
    if (!isRecord(collection)) return [];

    const possibleCollection = keys
        .map((key) => collection[key])
        .find(Array.isArray);

    return Array.isArray(possibleCollection) ? (possibleCollection as T[]) : [];
};

const getMessageText = (message: MessageLike): string =>
    getString(message.Body) ||
    getString(message.body) ||
    getString(message.message) ||
    getString(message.text) ||
    (isRecord(message.content) ? getString(message.content.text) : undefined) ||
    getString(message.content) ||
    '';

import { getWarboxMessageMediaItems } from './warboxMessageMedia';
import { parseNormalizedPhone } from 'src/modules/phones/utils/parseNormalizedPhone';
import { ECountryCode } from 'src/modules/forms/fields/AreaCodeTextField/components/AreaCodeSelect';
import { ThreadKind } from '../types/conversation';

const getMessageMedia = (message: MessageLike): string => {
    const warboxMedia = getWarboxMessageMediaItems(message)[0];
    if (warboxMedia?.url) return warboxMedia.url;

    return (
        getString(message.MediaUrl0) ||
        getString(message.mediaUrl) ||
        getString(message.media?.url) ||
        ''
    );
};

const getMessageMediaContentType = (message: MessageLike): string => {
    const warboxMedia = getWarboxMessageMediaItems(message)[0];
    if (warboxMedia?.contentType) return warboxMedia.contentType;

    return (
        getString(message.MediaContentType0) ||
        getString(message.mediaContentType) ||
        getString(message.media?.contentType) ||
        ''
    );
};

const getMessageOrigin = (message: MessageLike): MessageOrigin => {
    const direction = String(
        message.direction || message.Direction || ''
    ).toLowerCase();

    return direction.includes('out') ? 'warbox' : 'customer';
};

const getMessageClientId = (message: MessageLike): string | undefined =>
    isRecord(message.metadata)
        ? getString((message.metadata as UnknownRecord).clientMessageId)
        : undefined;

const getConversationName = (conversation: ConversationLike): string =>
    getString(conversation.name) ||
    getString(conversation.contact?.name) ||
    getString(conversation.contact?.displayName) ||
    (isRecord(conversation.lead)
        ? getString((conversation.lead as ContactLike).name)
        : undefined) ||
    getString(conversation.client?.name) ||
    getString(conversation.phone) ||
    getString(conversation.from) ||
    getString(conversation.to) ||
    'Sin nombre';

const getConversationPhone = (conversation: ConversationLike): string => {
    const contactNormalized = parseNormalizedPhone(
        conversation.contact?.normalizedPhone
    );
    const recipientNormalized = parseNormalizedPhone(
        conversation.recipient?.normalizedPhone
    );

    return (
        contactNormalized.phone ||
        recipientNormalized.phone ||
        getString(conversation.phone) ||
        getString(conversation.contact?.phone) ||
        getString(conversation.from) ||
        getString(conversation.to) ||
        ''
    );
};

const getConversationNormalizedPhone = (
    conversation: ConversationLike
): string | undefined => {
    const contactNormalized = parseNormalizedPhone(
        conversation.contact?.normalizedPhone
    );
    const recipientNormalized = parseNormalizedPhone(
        conversation.recipient?.normalizedPhone
    );

    return contactNormalized.phone || recipientNormalized.phone;
};

const getConversationContactCountryCode = (
    conversation: ConversationLike
): ECountryCode | undefined => {
    const contactNormalized = parseNormalizedPhone(
        conversation.contact?.normalizedPhone
    );
    const recipientNormalized = parseNormalizedPhone(
        conversation.recipient?.normalizedPhone
    );

    return contactNormalized.countryCode || recipientNormalized.countryCode;
};

const getConversationArea = (conversation: ConversationLike): string =>
    getString(conversation.area) ||
    (isRecord(conversation.area)
        ? getString(conversation.area.name)
        : undefined) ||
    '';

const getConversationSenderPhone = (conversation: ConversationLike): string => {
    const providers = Array.isArray(conversation.providers)
        ? conversation.providers
        : [];
    const lastProvider = getString(conversation.lastProvider);
    const providerByLastProvider = providers.find(
        (provider) => provider.name === lastProvider
    );

    return (
        getString(providerByLastProvider?.senderPhone) ||
        getString(providers[0]?.senderPhone) ||
        ''
    );
};

const getStoreAreas = (conversation: ConversationLike): StoreAreaLike[] => {
    const store = conversation.store;

    if (!isRecord(store) || !Array.isArray(store.areas)) return [];

    return store.areas as StoreAreaLike[];
};

const getConversationStoreArea = (
    conversation: ConversationLike
): StoreAreaLike | undefined => {
    const areas = getStoreAreas(conversation);
    const conversationArea = getConversationArea(conversation);

    return areas.find((area) => area.name === conversationArea);
};

const canSendConversationMessage = (
    conversation: ConversationLike,
    senderPhone: string
): boolean => {
    const areas = getStoreAreas(conversation);

    if (!areas.length) return true;

    const conversationArea = getConversationArea(conversation);
    const storeArea = areas.find((area) => area.name === conversationArea);

    if (!storeArea) return true;
    if (!isChannelsModeArea(storeArea)) return false;

    const activeWhatsappChannels = getActiveAreaChannels(
        storeArea.conversationChannels
    );

    if (!senderPhone) return activeWhatsappChannels.length > 0;

    return activeWhatsappChannels.some(
        (channel) =>
            normalizePhone(channel.sender?.phone) ===
            normalizePhone(senderPhone)
    );
};

const getLastMessage = (conversation: ConversationLike): MessageLike => {
    if (isRecord(conversation.lastMessage)) {
        return conversation.lastMessage as MessageLike;
    }

    if (Array.isArray(conversation.messages) && conversation.messages[0]) {
        return conversation.messages[0];
    }

    return conversation.latestMessage || conversation.message || {};
};

const hasAssignedLead = (conversation: ConversationLike): boolean => {
    if (!conversation.lead) return false;
    if (typeof conversation.lead === 'string')
        return Boolean(conversation.lead);
    return (
        isRecord(conversation.lead) &&
        Boolean((conversation.lead as UnknownRecord)._id)
    );
};

const getPresentationCardUrl = (assignedTo: unknown): string | undefined => {
    if (typeof assignedTo === 'string' && assignedTo.trim()) {
        const value = assignedTo.trim();
        if (value.startsWith('http://') || value.startsWith('https://')) {
            return value;
        }
        return undefined;
    }

    if (isRecord(assignedTo)) {
        return getString(assignedTo.presentationCard);
    }

    return undefined;
};

export const mapMessageToThreadMessage = (
    message: MessageLike
): ThreadMessage => {
    const id =
        getId(message._id) ||
        getId(message.id) ||
        getId(message.wspId) ||
        `${message.createdAt || message.created_at || Date.now()}`;
    const clientMessageId = getMessageClientId(message);
    const firstReaction = Array.isArray(message.reactions)
        ? message.reactions.find((reaction) => getString(reaction?.emoji))
        : undefined;

    return {
        id,
        renderKey: clientMessageId || id,
        name:
            getString(message.name) ||
            getString(message.Author) ||
            getString(message.author) ||
            getString(message.from) ||
            getString(message.From) ||
            '',
        message: getMessageText(message),
        mediaUrl: getMessageMedia(message),
        mediaContentType: getMessageMediaContentType(message),
        origin: getMessageOrigin(message),
        createdAt: message.createdAt || message.created_at || new Date(),
        isSeen: message.status === 'read' || Boolean(message.isSeen),
        status: getString(message.status),
        kind: message.kind,
        reaction:
            getString(message.reaction) || getString(firstReaction?.emoji),
        reactions: message.reactions,
    };
};

export const mapConversationToThread = (
    conversation: ConversationLike
): ThreadSummary => {
    const lastMessage = getLastMessage(conversation);
    const conversationName = getConversationName(conversation);
    const senderPhone = getConversationSenderPhone(conversation);
    const storeArea = getConversationStoreArea(conversation);

    return {
        _id: getString(conversation._id) || getString(conversation.id) || '',
        name: conversationName,
        phone: getConversationPhone(conversation),
        normalizedPhone: getConversationNormalizedPhone(conversation),
        contactCountryCode: getConversationContactCountryCode(conversation),
        senderPhone,
        callerPhone: getString(storeArea?.phone),
        storeCountryCode: isRecord(conversation.store)
            ? getString(conversation.store.countryCode)
            : undefined,
        linkedContactId: getDocumentId(conversation.linkedContact),
        storeId: getDocumentId(conversation.store) || '',
        storeName: isRecord(conversation.store)
            ? getString(conversation.store.name) || ''
            : '',
        area: getConversationArea(conversation),
        image:
            getString(conversation.image) ||
            getString(conversation.contact?.image) ||
            (isRecord(conversation.lead)
                ? getString((conversation.lead as ContactLike).image)
                : undefined) ||
            '',
        isSilenced: Boolean(conversation.isSilenced),
        isUsingChatBot: Boolean(
            conversation.isUsingChatBot || conversation.isChatBotActive
        ),
        canSendMessages: canSendConversationMessage(conversation, senderPhone),
        presentationCardUrl: getPresentationCardUrl(conversation.assignedTo),
        hasLead: hasAssignedLead(conversation),
        status: getString(conversation.status),
        channel:
            getString(conversation.channel) ||
            getString(conversation.provider) ||
            '',
        tags: Array.isArray(conversation.tags) ? conversation.tags : [],
        isRead: Boolean(conversation.isRead),
        lastMessage: {
            id: getString(lastMessage._id) || getString(lastMessage.id) || '',
            renderKey:
                getMessageClientId(lastMessage) ||
                getString(lastMessage._id) ||
                getString(lastMessage.id) ||
                '',
            name: getString(lastMessage.name) || conversationName,
            message:
                getString(conversation.lastMessagePreview) ||
                getMessageText(lastMessage) ||
                getString(conversation.lastMessageText) ||
                getString(conversation.lastMessageBody) ||
                '',
            mediaUrl: getMessageMedia(lastMessage),
            mediaContentType: getMessageMediaContentType(lastMessage),
            origin: getMessageOrigin(lastMessage),
            createdAt:
                lastMessage.createdAt ||
                conversation.lastMessageAt ||
                conversation.updatedAt ||
                conversation.createdAt ||
                new Date(),
            isSeen: Boolean(conversation.isRead),
            status: getString(lastMessage.status),
            kind: lastMessage.kind,
        },
    };
};

export const mapConversationsToThreads = (
    conversations: unknown
): ThreadSummary[] =>
    normalizeCollection<ConversationLike>(conversations, [
        'data',
        'conversations',
        'items',
        'results',
        'docs',
    ])
        .map(mapConversationToThread)
        .filter((thread) => thread._id);

export const mapMessagesToThreadMessages = (
    messages: unknown
): ThreadMessage[] =>
    normalizeCollection<MessageLike>(messages, [
        'data',
        'messages',
        'items',
        'results',
        'docs',
    ]).map(mapMessageToThreadMessage);
